'use client';

import React, { useEffect, useRef, useState } from 'react';

interface Testimonial {
  name: string;
  role: string;
  rating: number;
  text: string;
  initials: string;
}

const testimonials: Testimonial[] = [
  {
    name: 'Sarah M.',
    role: 'Patient since 2021',
    rating: 5,
    text: 'The team at Dental Center of Huntington is absolutely wonderful. From the moment I walked in, I felt welcomed and at ease. The care I received was exceptional — professional, thorough, and genuinely compassionate.',
    initials: 'SM',
  },
  {
    name: 'James T.',
    role: 'Patient since 2020',
    rating: 5,
    text: 'I had been avoiding the dentist for years due to anxiety, but this practice changed everything. The staff is incredibly patient and understanding. My smile has never looked better!',
    initials: 'JT',
  },
  {
    name: 'Linda R.',
    role: 'Patient since 2022',
    rating: 5,
    text: 'Outstanding dental care! The technology they use is state-of-the-art, and the results speak for themselves. I highly recommend Dental Center of Huntington to anyone looking for quality dental care.',
    initials: 'LR',
  },
  {
    name: 'Michael K.',
    role: 'Patient since 2019',
    rating: 5,
    text: 'I brought my whole family here and we couldn\'t be happier. The doctors are knowledgeable, the office is spotless, and the staff always goes above and beyond. Truly the best dental practice in the area.',
    initials: 'MK',
  },
  {
    name: 'Patricia H.',
    role: 'Patient since 2023',
    rating: 5,
    text: 'After my orthodontic treatment here, I finally have the smile I always dreamed of. The process was smooth, the team was supportive throughout, and the results exceeded my expectations.',
    initials: 'PH',
  },
  {
    name: 'David W.',
    role: 'Patient since 2021',
    rating: 5,
    text: 'Needed an emergency extraction and they fit me in the same day. The procedure was painless and the follow-up care was excellent. This is the kind of dental practice that truly cares about its patients.',
    initials: 'DW',
  },
];

function StarRating({ rating }: { rating: number }) {
  return (
    <div data-component-id="src/components/TestimonialsSection.tsx:60:4" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="60" data-component-end-line="73" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-1%20mb-4%22%7D" className="flex gap-1 mb-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg data-component-id="src/components/TestimonialsSection.tsx:62:8" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="62" data-component-end-line="71" data-component-file="TestimonialsSection.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D"
          key={i}
          className={`w-5 h-5 ${
            i < rating ? 'text-yellow-400' : 'text-gray-300'
          }`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path data-component-id="src/components/TestimonialsSection.tsx:70:10" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="70" data-component-end-line="70" data-component-file="TestimonialsSection.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

export default function TestimonialsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section data-component-id="src/components/TestimonialsSection.tsx:96:4" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="96" data-component-end-line="161" data-component-file="TestimonialsSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22id%22%3A%22testimonials%22%2C%22className%22%3A%22py-20%22%7D"
      id="testimonials"
      ref={sectionRef}
      className="py-20"
      style={{ backgroundColor: '#fafafa' }}
    >
      <div data-component-id="src/components/TestimonialsSection.tsx:102:6" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="102" data-component-end-line="160" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22section-container%22%7D" className="section-container">
        {/* Header */}
        <div data-component-id="src/components/TestimonialsSection.tsx:104:8" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="104" data-component-end-line="123" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          className={`text-center mb-14 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p data-component-id="src/components/TestimonialsSection.tsx:109:10" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="109" data-component-end-line="111" data-component-file="TestimonialsSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20uppercase%20tracking-widest%20text-gray-500%20mb-3%22%2C%22textContent%22%3A%22Patient%20Stories%22%7D" className="text-sm uppercase tracking-widest text-gray-500 mb-3" style={{ letterSpacing: '0.2em' }}>
            Patient Stories
          </p>
          <h2 data-component-id="src/components/TestimonialsSection.tsx:112:10" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="112" data-component-end-line="117" data-component-file="TestimonialsSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-semibold%20text-black%20mb-4%22%2C%22textContent%22%3A%22What%20Our%20Patients%20Say%22%7D"
            className="font-semibold text-black mb-4"
            style={{ fontSize: 'clamp(26px, 3vw, 38px)' }}
          >
            What Our Patients Say
          </h2>
          <div data-component-id="src/components/TestimonialsSection.tsx:118:10" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="118" data-component-end-line="118" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22section-divider%20mx-auto%22%7D" className="section-divider mx-auto" />
          <p data-component-id="src/components/TestimonialsSection.tsx:119:10" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="119" data-component-end-line="122" data-component-file="TestimonialsSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-gray-600%20max-w-xl%20mx-auto%22%2C%22textContent%22%3A%22Real%20experiences%20from%20real%20patients.%20We%20are%20proud%20to%20have%20earned%20the%20trust%20of%5Cn%20%20%20%20%20%20%20%20%20%20%20%20hundreds%20of%20families%20in%20the%20Shelton%2C%20CT%20area.%22%7D" className="text-gray-600 max-w-xl mx-auto" style={{ fontSize: '18px', lineHeight: 1.8 }}>
            Real experiences from real patients. We are proud to have earned the trust of
            hundreds of families in the Shelton, CT area.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div data-component-id="src/components/TestimonialsSection.tsx:126:8" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="126" data-component-end-line="159" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, idx) => (
            <div data-component-id="src/components/TestimonialsSection.tsx:128:12" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="128" data-component-end-line="157" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              key={idx}
              className={`testimonial-card transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              <StarRating data-component-id="src/components/TestimonialsSection.tsx:135:14" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="135" data-component-end-line="135" data-component-file="TestimonialsSection.tsx" data-component-name="StarRating" data-component-content="%7B%22elementName%22%3A%22StarRating%22%7D" rating={t.rating} />
              <p data-component-id="src/components/TestimonialsSection.tsx:136:14" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="136" data-component-end-line="141" data-component-file="TestimonialsSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-gray-700%20mb-6%20italic%22%2C%22textContent%22%3A%22%E2%80%9C%20%E2%80%9D%22%7D"
                className="text-gray-700 mb-6 italic"
                style={{ fontSize: '16px', lineHeight: 1.8 }}
              >
                &ldquo;{t.text}&rdquo;
              </p>
              <div data-component-id="src/components/TestimonialsSection.tsx:142:14" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="142" data-component-end-line="156" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%22%7D" className="flex items-center gap-3">
                <div data-component-id="src/components/TestimonialsSection.tsx:143:16" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="143" data-component-end-line="147" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-10%20h-10%20rounded-full%20bg-black%20text-white%20flex%20items-center%20justify-center%20font-semibold%20text-sm%20flex-shrink-0%22%7D"
                  className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center font-semibold text-sm flex-shrink-0"
                >
                  {t.initials}
                </div>
                <div data-component-id="src/components/TestimonialsSection.tsx:148:16" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="148" data-component-end-line="155" data-component-file="TestimonialsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                  <p data-component-id="src/components/TestimonialsSection.tsx:149:18" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="149" data-component-end-line="151" data-component-file="TestimonialsSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-semibold%20text-black%22%7D" className="font-semibold text-black" style={{ fontSize: '15px' }}>
                    {t.name}
                  </p>
                  <p data-component-id="src/components/TestimonialsSection.tsx:152:18" data-component-path="src/components/TestimonialsSection.tsx" data-component-line="152" data-component-end-line="154" data-component-file="TestimonialsSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-gray-500%22%7D" className="text-gray-500" style={{ fontSize: '13px' }}>
                    {t.role}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
